import {
  differenceInCalendarDays,
  addDays as addDaysFns,
  subDays,
  addMonths as addMonthsFns,
  startOfMonth as startOfMonthFns,
  endOfMonth as endOfMonthFns,
  startOfYear as startOfYearFns,
  endOfYear as endOfYearFns,
  isBefore as isBeforeFns,
  isAfter as isAfterFns,
  isWithinInterval,
  format
} from 'date-fns';
import { UTCDate } from '@date-fns/utc';
import { ISODateString, ISODateRange } from './types';

// Conversion
export function toISODateString(value: Date | UTCDate): ISODateString {
  return toISO(value);
}

export function fromTimestamp(ms: number): ISODateString {
  return toISO(new UTCDate(ms));
}

export function today(): ISODateString {
  return toISO(new UTCDate());
}

export function isValidISODateString(value: string): value is ISODateString {
  return ISODateString.safeParse(value).success;
}

export function toUTCDate(iso: ISODateString): UTCDate {
  return new UTCDate(iso);
}

// Formatting
export function formatDate(date: ISODateString, formatStr: string = 'MMM do yyyy'): string {
  return format(toDate(date), formatStr);
}

export function formatDateOrDash(
  date: ISODateString | null | undefined,
  formatStr: string = 'MMM do yyyy'
): string {
  return date ? formatDate(date, formatStr) : '-';
}

export function formatRange(
  range: ISODateRange,
  formatStr: string = 'MMM d, yyyy',
  separator: string = ' – '
): string {
  return `${formatDate(range.from, formatStr)}${separator}${formatDate(range.to, formatStr)}`;
}

// Calculations
export function daysBetween(from: ISODateString, to: ISODateString): number {
  return differenceInCalendarDays(toDate(to), toDate(from)) + 1;
}

export function addDays(date: ISODateString, days: number): ISODateString {
  return toISO(addDaysFns(toDate(date), days));
}

export function subtractDays(date: ISODateString, days: number): ISODateString {
  return toISO(subDays(toDate(date), days));
}

export function addMonths(date: ISODateString, months: number): ISODateString {
  return toISO(addMonthsFns(toDate(date), months));
}

export function startOfMonth(date: ISODateString): ISODateString {
  return toISO(startOfMonthFns(toDate(date)));
}

export function endOfMonth(date: ISODateString): ISODateString {
  return toISO(endOfMonthFns(toDate(date)));
}

export function startOfYear(date: ISODateString): ISODateString {
  return toISO(startOfYearFns(toDate(date)));
}

export function endOfYear(date: ISODateString): ISODateString {
  return toISO(endOfYearFns(toDate(date)));
}

// Comparisons
export function isBefore(a: ISODateString, b: ISODateString): boolean {
  return isBeforeFns(toDate(a), toDate(b));
}

export function isAfter(a: ISODateString, b: ISODateString): boolean {
  return isAfterFns(toDate(a), toDate(b));
}

export function isBeforeOrEqual(a: ISODateString, b: ISODateString): boolean {
  return a <= b;
}

export function isAfterOrEqual(a: ISODateString, b: ISODateString): boolean {
  return a >= b;
}

export function isWithinRange(date: ISODateString, range: ISODateRange): boolean {
  return isWithinInterval(toDate(date), { start: toDate(range.from), end: toDate(range.to) });
}

function toDate(dateStr: ISODateString): UTCDate {
  return new UTCDate(dateStr);
}

function toISO(date: Date | UTCDate): ISODateString {
  return format(date, 'yyyy-MM-dd');
}
